/**
 * Format a target ISO timestamp as a short countdown string.
 *
 * Lifted from `web/src/admin/EventsListPage.tsx:123-139` (Story 6.2 CSD
 * #12 forward-deferral note) when Story 7.2 became the THIRD consumer
 * (EventsListPage card countdown + dashboard top-bar countdown +
 * EventPhasePill formatting). Story 8.10 becomes the FOURTH consumer.
 *
 * Examples:
 *   "1h 42m left"
 *   "12m left"
 *   "<1m left"
 *   "closed"
 *
 * [Source: web/src/admin/EventsListPage.tsx:123-139 — original location]
 * [Source: _bmad-output/implementation-artifacts/6-2-implement-get-admin-events-and-events-list-page.md — CSD #12]
 * [Source: _bmad-output/implementation-artifacts/7-2-implement-admin-dashboard-page-with-polling-and-stale-banner.md — CSD #5]
 */
export function formatCountdown(
  targetIso: string,
  now: Date = new Date(),
): string {
  const target = new Date(targetIso).getTime();
  const diffMs = target - now.getTime();
  if (diffMs <= 0) {
    return 'closed';
  }
  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${remainingMins}m left`;
  }
  if (minutes > 0) {
    return `${minutes}m left`;
  }
  return '<1m left';
}

/**
 * Story 8.10 — return `true` iff the target ISO timestamp is in the
 * future AND within `thresholdMs` of `now`. Used to gate the <1h
 * countdown line on the submitter pre-open + the persistent footer
 * countdown (UX §4.5 + epic AC `epics.md:1790, 1792`).
 *
 * Returns `false` when:
 *   - target is in the past (diff <= 0).
 *   - target is more than thresholdMs away.
 *
 * Pure function; consumer passes `now` for testability.
 *
 * [Source: _bmad-output/implementation-artifacts/8-10-implement-submitter-lifecycle-mismatched-states.md — CSD #2]
 */
export function isWithinCountdownThreshold(
  targetIso: string,
  thresholdMs: number,
  now: Date = new Date(),
): boolean {
  const target = new Date(targetIso).getTime();
  const diffMs = target - now.getTime();
  return diffMs > 0 && diffMs <= thresholdMs;
}

/**
 * Story 8.10 — render an ISO timestamp as a short locale-aware date+time
 * string for lifecycle copy (e.g., "Submissions open at <time>",
 * "Submissions close at <time>"). Lifted verbatim from
 * `web/src/submitter/PreLinkPage.tsx:155-166` (Story 8.4 origin) so the
 * Story 8.10 `<SubmissionsCountdownFooter>` can share the same helper
 * (CSD #2 sub-lock — topical co-location with `formatCountdown`).
 *
 * Returns the original ISO string verbatim when (a) the input is not a
 * valid date OR (b) `Intl.DateTimeFormat` throws. NaN guard prevents the
 * "Invalid Date" string from leaking into user-facing copy.
 *
 * [Source: web/src/submitter/PreLinkPage.tsx:155-166 — Story 8.4 origin]
 * [Source: _bmad-output/implementation-artifacts/8-10-implement-submitter-lifecycle-mismatched-states.md — CSD #2 sub-lock + CSD #11]
 */
export function formatLifecycleTime(iso: string): string {
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return iso;
  }
}
